import { useCallback } from "react";
import uploadFile from "../firebase/uploadFile";
import useAppDispatch from "../hooks/useAppDispatch";
import {
  setSnackbarMessage,
  setSnackbarState,
  setSnackbarStatus,
  setViewPage,
  setViewQuery,
} from "../redux/slices/main";
import { useNavigate } from "react-router-dom";
import { Publication, UploadStatus } from "../types/app";

export function SnackbarFns() {
  const dispatch = useAppDispatch();

  const handleSnackbar = useCallback(
    (
      uploadStatus: UploadStatus,
      snackbarMessage: string,
      snackbarState: boolean
    ) => {
      dispatch(setSnackbarStatus(uploadStatus));
      dispatch(setSnackbarMessage(snackbarMessage));
      dispatch(setSnackbarState(snackbarState));
    },
    [dispatch]
  );

  return handleSnackbar;
}

export function useFileUpload() {
  const dispatch = useAppDispatch();
  const handleSnackbar = SnackbarFns();

  const handleFileUpload = useCallback(
    (uploadType: string, files: File[] | Publication[]) => {
      handleSnackbar("warning" as UploadStatus, "Uploading files...", true);

      let uploadPromises = [];

      if (uploadType === "file") {
        let uploadedSize = 0;

        uploadPromises = (files as File[]).map((file, idx) => {
          const currSize = file.size / (1024 * 1024);
          uploadedSize += currSize;
          return uploadFile(file, idx, uploadedSize - currSize);
        });
      } else {
        uploadPromises = (files as Publication[]).map((publication, idx) => {
          return uploadFile(publication.ePrintUrl, idx, 0, publication.title);
        });
      }

      Promise.allSettled(uploadPromises).then((results) => {
        const successCount = results.filter(
          (result) => result.status === "fulfilled"
        ).length;
        if (successCount === files.length) {
          // All files uploaded successfully
          handleSnackbar("success", "All files uploaded successfully", true);
        } else {
          let failedMessages = "Upload failed for: ";
          results.forEach((result, idx) => {
            if (result.status === "rejected") {
              const fileName =
                uploadType === "file"
                  ? (files[idx] as File).name
                  : (files[idx] as Publication).title;
              failedMessages += fileName + " " + result.reason + " ";
            }
          });
          handleSnackbar("error", failedMessages, true);
        }
      });
    },
    [dispatch, handleSnackbar]
  );

  return handleFileUpload;
}

export function ViewFile() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onClick = useCallback(
    (description: string[], pdfName: string, page: number) => {
      dispatch(setViewQuery(description));
      dispatch(setViewPage(page));
      navigate(`/${pdfName}`);
    },
    [dispatch]
  );

  return onClick;
}
